import { useMemo, useState } from "react";
import type { OpsApi, SortMode } from "../useOps";
import { SEVERITY_COLOR, SEVERITY_RANK, TRADING_AREAS, RULE_AREA, type Severity, type Signal } from "@/types/api";
import { ruleTag, signalAge } from "@/lib/normalize";

const SEVERITIES: Severity[] = ["critical", "high", "medium", "low"];

const ACTIVE_HOLD_MS: Record<string, number> = {
  critical: 24 * 3600 * 1000,
  high: 3 * 3600 * 1000,
};
const UNRELIABLE_TTL_MS = 3600 * 1000;
// Archive shows the trailing 60 days only — older items drop off the list (deep links
// and share pages still resolve them; this bounds the feed, not the record).
const ARCHIVE_RETENTION_MS = 60 * 24 * 3600 * 1000;

function isActive(s: Signal, now: number): boolean {
  if (s.status === 'live') {
    if (s.statusReliable) return true;
    return now - s.createdAt < UNRELIABLE_TTL_MS;
  }
  const hold = ACTIVE_HOLD_MS[s.severity];
  if (hold && s.resolvedAt && now - s.resolvedAt < hold) return true;
  return false;
}

function sortList(list: Signal[], mode: SortMode): void {
  // Time = when the situation HAPPENED (createdAt), not when it last revised — a 10h-old
  // outage getting a new filing must not leapfrog a fresh event.
  if (mode === "severity") {
    list.sort(
      (a, b) =>
        SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] ||
        b.createdAt - a.createdAt,
    );
  } else {
    list.sort(
      (a, b) =>
        b.createdAt - a.createdAt ||
        SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity],
    );
  }
}

/**
 * SignalFeed — the product's primary column. Splits signals into Active and
 * Archive sections with severity-based retention.
 */
export function SignalFeed({ ops }: { ops: OpsApi }) {
  const now = Date.now();
  const { active, archive } = useMemo(() => {
    const filtered = ops.signals.filter((s) => {
      if (!ops.sevFilter[s.severity]) return false;
      if (s.severity === "high" || s.severity === "critical") return true;
      const area = RULE_AREA[s.rule];
      return area ? ops.enabledAreas.has(area) : true;
    });
    const a: Signal[] = [];
    const b: Signal[] = [];
    for (const s of filtered) {
      if (isActive(s, now)) {
        a.push(s);
        continue;
      }
      const archivedAt = s.resolvedAt ?? s.updatedAt ?? s.createdAt;
      if (now - archivedAt < ARCHIVE_RETENTION_MS) b.push(s);
    }
    sortList(a, ops.sortMode);
    sortList(b, ops.sortMode);
    return { active: a, archive: b };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ops.signals, ops.sortMode, ops.sevFilter, ops.enabledAreas, ops.version]);

  return (
    <aside id="feed">
      <div className="feed-head">
        <div className="feed-title">
          <div className="lead">
            <span className="n">SIGNALS</span>
            <span className="live">LIVE</span>
          </div>
          <span className="cnt">
            {active.length + archive.length} / {ops.signals.length}
          </span>
        </div>
        <div className="feed-controls">
          <div className="filters">
            {SEVERITIES.map((sv) => (
              <div
                key={sv}
                className={`fchip${ops.sevFilter[sv] ? "" : " off"}`}
                onClick={() => ops.toggleSeverity(sv)}
                title={`${ops.sevFilter[sv] ? "Hide" : "Show"} ${sv}-severity signals`}
              >
                <span className="sw" style={{ background: SEVERITY_COLOR[sv] }} />
                {sv}
              </div>
            ))}
          </div>
          <div className="sortseg" title="Sort signals by time or severity">
            {(["time", "severity"] as SortMode[]).map((m) => (
              <button
                key={m}
                className={ops.sortMode === m ? "on" : ""}
                onClick={() => ops.setSortMode(m)}
                title={m === "time" ? "Sort by most recent" : "Sort by highest severity first"}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <TradingAreaFilter ops={ops} />
      </div>
      <div className="feed-list">
        {active.length > 0 && (
          <div className="feed-section-label" style={{ color: 'var(--green)', borderBottom: '1px solid rgba(var(--green-rgb), 0.2)', padding: '6px 12px', fontSize: 9, letterSpacing: '0.12em', fontFamily: 'var(--mono)' }}>
            ACTIVE · {active.length}
          </div>
        )}
        {active.map((s) => (
          <SignalRow key={s.id} s={s} ops={ops} />
        ))}
        {archive.length > 0 && (
          <div className="feed-section-label" style={{ color: 'var(--ivory-d)', borderBottom: '1px solid var(--line)', padding: '6px 12px', fontSize: 9, letterSpacing: '0.12em', fontFamily: 'var(--mono)', marginTop: active.length > 0 ? 4 : 0 }}>
            ARCHIVE · {archive.length}
          </div>
        )}
        {archive.map((s) => (
          <SignalRow key={s.id} s={s} ops={ops} archived />
        ))}
      </div>
      <div className="rail-handle left" id="feedHandle" />
    </aside>
  );
}

function SignalRow({ s, ops, archived }: { s: Signal; ops: OpsApi; archived?: boolean }) {
  return (
    <div
      className={
        "sig" +
        (s.id === ops.selectedSignalId ? " sel" : "") +
        (s.severity === "critical" ? " crit" : "") +
        (archived ? " archived" : "")
      }
      style={{
        ["--sev" as string]: SEVERITY_COLOR[s.severity],
        // Archive rows read as history at a glance: heavily dimmed + desaturated (hover restores
        // a little via .sig.archived:hover in global.css).
        opacity: archived ? 0.35 : 1,
        filter: archived ? "grayscale(0.7)" : undefined,
      }}
      onClick={() => ops.selectSignal(s.id)}
      title={`${s.severity.toUpperCase()} · ${s.title} — click for detail`}
    >
      <div className="r1">
        <span className="sevtag">{s.severity}</span>
        {s.status === 'resolved' && <span className="lifecycle-badge resolved" style={{ marginLeft: 4 }}>RESOLVED</span>}
        {s.status === 'live' && !s.statusReliable && <span className="lifecycle-badge unknown" style={{ marginLeft: 4 }}>STATUS UNKNOWN</span>}
        {/* The two numbers we sell, on the row itself — not just detail/landing surfaces. */}
        {(s.confidenceRate != null || s.estimatedEffect) && (
          <span className="row-score" title="Confidence · estimated tradeable 4h effect (category track record)">
            {s.confidenceRate != null ? `${Math.round(s.confidenceRate * 100)}%` : ""}
            {s.confidenceRate != null && s.estimatedEffect ? " · " : ""}
            {s.estimatedEffect ? `~${s.estimatedEffect.sigma.toFixed(1)}σ` : ""}
          </span>
        )}
        <span className="time">{signalAge(s.createdAt, s.updatedAt)}</span>
      </div>
      <div className="ttl">{s.title}</div>
      <div className="r3">
        {s.tickers.length ? (
          <div className="tickers">
            {s.tickers.slice(0, 6).map((t) => (
              <span className="tk" key={t}>
                {t}
              </span>
            ))}
          </div>
        ) : (
          <span className="region-x">{s.region}</span>
        )}
        <span className="rev">{ruleTag(s.rule)}</span>
      </div>
    </div>
  );
}

/**
 * TradingAreaFilter — opt-in desk packs. The core feed always shows high/critical
 * signals across every area; enabling an area (Energy, Maritime, …) ADDS that
 * desk's full lower-severity detail. Off by default; persisted in localStorage.
 */
function TradingAreaFilter({ ops }: { ops: OpsApi }) {
  const [open, setOpen] = useState(false);
  const active = ops.enabledAreas;
  return (
    <div className="feed-events">
      <button
        className="fe-head"
        onClick={() => setOpen((o) => !o)}
        title="Core feed shows high/critical signals. Enable your desk's areas to add their full lower-severity detail."
      >
        <span>Trading areas</span>
        <span className="fe-cnt">
          {active.size ? `core +${active.size}` : "core only"} {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div className="filters">
          {TRADING_AREAS.map((a) => (
            <div
              key={a.area}
              className={`fchip${active.has(a.area) ? "" : " off"}`}
              onClick={() => ops.toggleArea(a.area)}
            >
              {a.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
